import { useCallback, useMemo, useState } from "react"

import { Tab } from "./HomePage.logic"

import { convertGraphQLDateToDate } from "@/utils/date"

/**
 * 並び替えキー。
 *
 * - DATE_VISIT: 来店予定日
 * - PAID_TIME: 回収日 (回収済タブのみ値が入る)
 */
export type CancelSortKey = "DATE_VISIT" | "PAID_TIME"

export type CancelSortOrder = "ASC" | "DESC"

export type CancelSort = {
	key: CancelSortKey
	order: CancelSortOrder
}

/** null は「並び替えなし」= サーバーから返ってきた順 (キャンセルの登録順) */
export type CancelSortState = CancelSort | null

export const CANCEL_SORT_KEY_LABEL: Record<CancelSortKey, string> = {
	DATE_VISIT: "来店予定日",
	PAID_TIME: "回収日",
}

export const CANCEL_SORT_ORDER_LABEL: Record<CancelSortOrder, string> = {
	ASC: "古い順",
	DESC: "新しい順",
}

/** 並び替えの対象になるキャンセル。CancelForListFragment を構造的に満たす */
type SortableCancel = {
	dateVisit: string
	paidTime: string | null
}

/**
 * タブごとに選べる並び替えキー。
 *
 * 回収日は回収済タブ以外では値が入らないため、そのタブでしか出さない。
 */
export const availableSortKeys = (tab: Tab): CancelSortKey[] => {
	switch (tab) {
		case "INVOICE_NOT_SENT":
		case "NOT_SUBJECT":
		case "INVOICE_SENT":
			return ["DATE_VISIT"]
		case "PAID":
			return ["DATE_VISIT", "PAID_TIME"]
		// 再予約系タブは並び替え対象外
		case "ENCOURAGING_RERESERVATION_NOT_SCHEDULED":
		case "ENCOURAGING_RERESERVATION_SCHEDULED":
		case "RERESERVED":
			return []
		default:
			return tab satisfies never
	}
}

// dateVisit は "YYYY-MM-DD" 固定なので辞書順比較で日付順になる。
// paidTime はタイムゾーン表記なしの "yyyy-mm-ddThh:mm:ss" で返ってくるため、
// 表示側 (CancelsTableView) と同じく JST 固定でパースして比較する
const sortValue = (
	cancel: SortableCancel,
	key: CancelSortKey
): string | number | null => {
	switch (key) {
		case "DATE_VISIT":
			return cancel.dateVisit
		case "PAID_TIME": {
			if (cancel.paidTime === null) {
				return null
			}
			const time = convertGraphQLDateToDate(cancel.paidTime).getTime()
			return Number.isNaN(time) ? null : time
		}
		default:
			return key satisfies never
	}
}

/**
 * キャンセル一覧を並び替えた新しい配列を返す。
 *
 * - sort が null のときは元の順序 (サーバーが返した順) をそのまま維持する
 * - 値が無い行 (回収日が未設定など) は昇順・降順どちらでも末尾に寄せる。
 *   日付が入っている行から先に見えるようにするため
 * - 同値の行の並びは元の配列の順序を保つ (Array.prototype.sort は安定ソート)
 */
export const sortCancels = <T extends SortableCancel>(
	cancels: readonly T[],
	sort: CancelSortState
): readonly T[] => {
	if (sort === null) {
		return cancels
	}

	const sign = sort.order === "ASC" ? 1 : -1
	return [...cancels].sort((a, b) => {
		const aValue = sortValue(a, sort.key)
		const bValue = sortValue(b, sort.key)
		if (aValue === null && bValue === null) {
			return 0
		}
		if (aValue === null) {
			return 1
		}
		if (bValue === null) {
			return -1
		}
		if (aValue < bValue) {
			return -1 * sign
		}
		if (aValue > bValue) {
			return 1 * sign
		}
		return 0
	})
}

/**
 * 並び替えの状態を持つ hook。
 *
 * 同じキーのボタンを押すたびに 降順 → 昇順 → 解除 と切り替わる。
 * 選択中のキーが使えないタブ (回収日順のまま未請求タブへ移動した場合など) では
 * sort を null として返すが、state 自体は消さないので、
 * 使えるタブに戻ってきたときは並び替えが復活する。
 */
export const useCancelSort = (currentTab: Tab) => {
	const [rawSort, setRawSort] = useState<CancelSortState>(null)

	const sortableKeys = useMemo(
		() => availableSortKeys(currentTab),
		[currentTab]
	)

	const sort = useMemo(() => {
		if (rawSort === null) {
			return null
		}
		return sortableKeys.includes(rawSort.key) ? rawSort : null
	}, [rawSort, sortableKeys])

	const toggleSort = useCallback((key: CancelSortKey) => {
		setRawSort((current) => {
			if (current === null || current.key !== key) {
				return { key, order: "DESC" }
			}
			return current.order === "DESC" ? { key, order: "ASC" } : null
		})
	}, [])

	return { sort, sortableKeys, toggleSort }
}
